
import { Options, Vue } from 'vue-class-component';

declare const grecaptcha: {
  render: (container: string, options: { 
    sitekey: string, 
    callback?: (token: string) => void;
    'error-callback'?: () => void;
    'expired-callback'?: () => void;
   }
  ) => number;
  reset: (id?: number) => void;
};

const SITE_KEY = (process as any).env?.VUE_APP_RECAPTCHA_SITEKEY as string | undefined;
if (!SITE_KEY) {
  throw new Error('process.env.RECAPTCHA_SITE_KEY is not defined');
}

@Options({
  props: {
    id: { type: String, default: 'google-recaptcha' }
  },
  emits: ["onSuccess", "onExpire", "onError"]
})
export default class ReCaptcha extends Vue {
  readonly id!: string;
  private widgetId: number | null = null;
  private scriptEl: HTMLScriptElement | null = null;

  private load(): Promise<void> {
    if (typeof grecaptcha !== 'undefined') return Promise.resolve();
    return new Promise((res, rej) => {
      (window as any).__recaptchaLoaded = () => res();
      this.scriptEl = document.createElement('script');
      this.scriptEl.src =
        'https://www.google.com/recaptcha/api.js?onload=__recaptchaLoaded&render=explicit';
      this.scriptEl.async = true;
      this.scriptEl.defer = true;
      this.scriptEl.onerror = () =>
        rej(new Error('Failed to load reCAPTCHA script'));
      document.head.appendChild(this.scriptEl);
    });
  }

  created() {
    this.load().catch(console.error);
  }

  async mounted() {
    await this.load();
    this.widgetId = grecaptcha.render(this.id, { 
      sitekey: SITE_KEY!, 
      callback: this.onSuccessReCaptcha,
      'error-callback': this.onErrorReCaptcha,
      'expired-callback': this.onExpireReCaptcha
    });
    this.$emit("grecaptcha", {
      grecaptcha,
      widgetId: this.widgetId,
      scriptEl: this.scriptEl
    });
  }

  unmounted() {
    if (this.widgetId !== null && typeof grecaptcha !== 'undefined') {
      grecaptcha.reset(this.widgetId);
    }
    if (this.scriptEl) this.scriptEl.remove();
    delete (window as any).__recaptchaLoaded;
  }

  onSuccessReCaptcha(token: any) {
    this.$emit('onSuccess', token);
  }

  onErrorReCaptcha() {
    this.$emit('onError');
  }

  onExpireReCaptcha() {
    this.$emit('onExpire');
  }
}
